
import { supabase } from '@/lib/customSupabaseClient';
import { processSaleData, processReceiptData } from '@/hooks/data-management/useDataProcessing';
import { calculateStatus } from './utils';
import { useSalesQuota } from '@/hooks/useSalesQuota';

// Helper para remover zeros à esquerda
const removeLeadingZeros = (value) => {
    if (!value) return value;
    return String(value).replace(/^0+/, '');
};

const CHUNK_SIZE = 50;

export const useDataMutation = ({ user, activeAccountId, toast, setSales, setReceipts, setOperators, setFlags, setPaymentMethods, setTotalSales, setTotalReceipts, openAliasModal, forceReloadData, salesPerPage, receiptsPerPage, ...dataRepos }) => {

    const { checkQuota } = useSalesQuota(activeAccountId);

    const handleAddFlag = async (newFlag) => {
        const { data, error } = await supabase.from('flags').insert({ name: newFlag.name, user_id: activeAccountId }).select().single();
        if (error) {
            if (error.code === '23505') {
                // Se já existe, busca a existente e retorna para vincular automaticamente
                const { data: existingData } = await supabase.from('flags').select('*').ilike('name', newFlag.name).eq('user_id', activeAccountId).single();
                if (existingData) return existingData;
            }
            toast({ title: "Erro ao adicionar bandeira", description: error.message, variant: "destructive" });
            return null;
        }
        setFlags(prev => {
            if (!prev.some(f => f.id === data.id)) return [...prev, data];
            return prev;
        });
        toast({ title: "Sucesso!", description: "Bandeira cadastrada." });
        return data;
    };

    const handleAddPaymentMethod = async (newMethod) => {
        const { data, error } = await supabase.from('payment_methods').insert({ name: newMethod.name, user_id: activeAccountId }).select().single();
        if (error) {
            if (error.code === '23505') {
                const { data: existingData } = await supabase.from('payment_methods').select('*').ilike('name', newMethod.name).eq('user_id', activeAccountId).single();
                if (existingData) return existingData;
            }
            toast({ title: "Erro ao adicionar forma de pagamento", description: error.message, variant: "destructive" });
            return null;
        }
        setPaymentMethods(prev => {
            if (!prev.some(p => p.id === data.id)) return [...prev, data];
            return prev;
        });
        toast({ title: "Sucesso!", description: "Forma de pagamento cadastrada." });
        return data;
    };

    const handleSaveSales = async (salesData, operatorId, callbacks) => {
        const { data: userAliases } = await supabase.from('aliases').select('*').eq('user_id', activeAccountId);
        const preloadedAliases = userAliases || [];
        const processingContext = { ...dataRepos, user, activeAccountId, flags: dataRepos.flags, operators: dataRepos.operators, paymentMethods: dataRepos.paymentMethods, handleAddFlag, handleAddPaymentMethod, operatorId, openAliasModal, preloadedAliases };

        let validSales = [];
        let failedRows = [];
        let validationErrorMessages = new Set();

        // Primeiro, processar todas as vendas para obter os dados completos
        for (const sale of salesData) {
            try {
                // SANITIZAÇÃO: Remover zeros à esquerda dos códigos antes de salvar
                if (sale.codigoautorizacao) sale.codigoautorizacao = removeLeadingZeros(sale.codigoautorizacao);
                if (sale.codigopedido) sale.codigopedido = removeLeadingZeros(sale.codigopedido);

                const processedSale = await processSaleData(sale, processingContext);
                validSales.push(processedSale);
            } catch (error) {
                failedRows.push(sale);
                validationErrorMessages.add(error.message);
            }
        }

        if (validationErrorMessages.size > 0) {
            const description = Array.from(validationErrorMessages).join(' | ');
            toast({ title: `Vendas inválidas ou não vinculadas`, description: description, variant: "destructive", duration: 8000 });
        }

        if (validSales.length > 0) {
            // === VERIFICAÇÃO DE COTA DE VENDAS ===
            const quotaCheck = await checkQuota(validSales.length);
            if (!quotaCheck.allowed) {
                toast({
                    title: "🚫 Limite de Vendas Atingido",
                    description: quotaCheck.message,
                    variant: "destructive",
                    duration: 12000,
                });
                callbacks?.onComplete?.(salesData); // Retorna tudo como "não gravado"
                return;
            }
            // === FIM DA VERIFICAÇÃO DE COTA ===

            let allNewSales = [];
            const totalChunks = Math.ceil(validSales.length / CHUNK_SIZE);

            try {
                for (let i = 0; i < validSales.length; i += CHUNK_SIZE) {
                    const chunk = validSales.slice(i, i + CHUNK_SIZE);
                    const currentChunk = Math.floor(i / CHUNK_SIZE) + 1;

                    const progress = Math.round(20 + (currentChunk / totalChunks) * 70);
                    callbacks?.onProgress?.(progress);

                    const { data: newSales, error: insertError } = await supabase.from('sales').upsert(chunk, { onConflict: 'user_id,data,valorcompra,codigoautorizacao,codigopedido,bandeira,tipotransacao,empresa', ignoreDuplicates: true }).select();

                    if (insertError) {
                        toast({ title: "Erro ao Salvar Vendas", description: `Falha no lote ${currentChunk}: ${insertError.message}`, variant: "destructive" });
                        const failedOriginals = salesData.filter(originalSale =>
                            chunk.some(processed => processed.originalIndex === originalSale.originalIndex)
                        );
                        failedRows.push(...failedOriginals);
                        continue;
                    }

                    allNewSales = [...allNewSales, ...newSales];
                }

                if (allNewSales.length > 0) {
                    forceReloadData();
                    toast({ title: "Sucesso!", description: `${allNewSales.length} vendas foram lançadas.` });
                }
            } catch (error) {
                console.error('Erro crítico ao salvar vendas:', error);
                toast({ title: "Erro Crítico", description: "Ocorreu um erro inesperado ao salvar as vendas.", variant: "destructive" });
            } finally {
                callbacks?.onSuccess?.(failedRows);
                callbacks?.onComplete?.(failedRows);
            }
        } else {
            callbacks?.onComplete?.(failedRows);
        }
    };

    const handleUpdateSale = async (saleData, saleId, callbacks) => {
        let hasError = false;
        try {
            const { data: userAliases } = await supabase.from('aliases').select('*').eq('user_id', activeAccountId);
            const preloadedAliases = userAliases || [];
            const processingContext = { ...dataRepos, user, activeAccountId, flags: dataRepos.flags, operators: dataRepos.operators, paymentMethods: dataRepos.paymentMethods, handleAddFlag, handleAddPaymentMethod, saleId, openAliasModal, preloadedAliases };

            if (saleData.codigoautorizacao) saleData.codigoautorizacao = removeLeadingZeros(saleData.codigoautorizacao);
            if (saleData.codigopedido) saleData.codigopedido = removeLeadingZeros(saleData.codigopedido);

            const processedData = await processSaleData(saleData, processingContext, true);
            const { data, error } = await supabase.from('sales').update(processedData).eq('id', saleId).select().single();
            if (error) {
                hasError = true;
                throw error;
            }

            setSales(prev => prev.map(s => s.id === data.id ? data : s));
            forceReloadData();
            toast({ title: "Sucesso!", description: "Venda atualizada." });
        } catch (error) {
            hasError = true;
            toast({ title: "Erro ao atualizar venda", description: error.message, variant: "destructive" });
        } finally {
            if (!hasError && callbacks?.onSuccess) {
                callbacks.onSuccess();
            }
        }
    };

    const handleDeleteSale = async (saleId) => {
        const { error } = await supabase.from('sales').delete().eq('id', saleId);
        if (error) {
            toast({ title: "Erro ao remover venda", description: error.message, variant: "destructive" });
        } else {
            forceReloadData();
            toast({ title: "Sucesso!", description: "Venda removida." });
        }
    };

    const handleDeleteSalesBatch = async (saleIds) => {
        const { error } = await supabase.from('sales').delete().in('id', saleIds);
        if (error) {
            toast({ title: "Erro ao remover vendas", description: error.message, variant: "destructive" });
        } else {
            forceReloadData();
            toast({ title: "Sucesso!", description: `${saleIds.length} vendas removidas.` });
        }
    };

    const handleSaveReceipts = async (receiptsData, operatorId, callbacks) => {
        let validReceipts = [];
        let failedRows = [];
        let validationErrorMessages = new Set();
        
        const { data: userAliases } = await supabase.from('aliases').select('*').eq('user_id', activeAccountId);
        const preloadedAliases = userAliases || [];
        
        const processingContext = { ...dataRepos, user, activeAccountId, flags: dataRepos.flags, paymentMethods: dataRepos.paymentMethods, handleAddFlag, handleAddPaymentMethod, operatorId, openAliasModal, preloadedAliases };

        for (const receipt of receiptsData) {
            try {
                if (receipt.codigo_autorizacao) receipt.codigo_autorizacao = removeLeadingZeros(receipt.codigo_autorizacao);
                if (receipt.codigopedido) receipt.codigopedido = removeLeadingZeros(receipt.codigopedido);

                const processedReceipt = await processReceiptData(receipt, processingContext);
                validReceipts.push(processedReceipt);
            } catch (error) {
                failedRows.push(receipt);
                validationErrorMessages.add(error.message);
            }
        }

        if (validationErrorMessages.size > 0) {
            const description = Array.from(validationErrorMessages).join(' | ');
            toast({ title: `Recebimentos inválidos`, description: description, variant: "destructive", duration: 8000 });
        }

        if (validReceipts.length > 0) {
            // === VERIFICAÇÃO DE COTA DE VENDAS (BLOQUEIA RECEBIMENTOS SE COTA ATINGIDA) ===
            const quotaCheck = await checkQuota(0);
            if (!quotaCheck.allowed) {
                toast({
                    title: "🚫 Limite do Plano Atingido",
                    description: quotaCheck.message,
                    variant: "destructive",
                    duration: 12000,
                });
                callbacks?.onComplete?.(receiptsData);
                return;
            }
            // === FIM DA VERIFICAÇÃO DE COTA ===

            let allNewReceipts = [];
            const totalChunks = Math.ceil(validReceipts.length / CHUNK_SIZE);

            try {
                for (let i = 0; i < validReceipts.length; i += CHUNK_SIZE) {
                    const chunk = validReceipts.slice(i, i + CHUNK_SIZE);
                    const currentChunk = Math.floor(i / CHUNK_SIZE) + 1;

                    const progress = Math.round(20 + (currentChunk / totalChunks) * 70);
                    callbacks?.onProgress?.(progress);

                    const { data: newReceipts, error: insertError } = await supabase.from('receipts').insert(chunk).select();

                    if (insertError) {
                        toast({ title: "Erro ao Salvar Recebimentos", description: `Falha no lote ${currentChunk}: ${insertError.message}`, variant: "destructive" });
                        failedRows.push(...receiptsData.filter(r => chunk.some(c => c.codigo_autorizacao === r.codigo_autorizacao && c.valor === r.valor)));
                        continue;
                    }

                    allNewReceipts = [...allNewReceipts, ...newReceipts];
                }

                if (allNewReceipts.length > 0) {
                    // Ler o modo de reconciliação com fallback seguro
                    let mode = user?.user_metadata?.reconciliation_mode || 'sum';

                    // Chamar o RPC para reconciliação em lote
                    const { error: rpcError } = await supabase.rpc('rpc_reconciliar_vendas', {
                        p_user_id: activeAccountId,
                        p_mode: mode
                    });

                    if (rpcError) {
                        toast({ title: "Erro na Conciliação", description: "Os recebimentos foram salvos, mas a atualização das vendas falhou: " + rpcError.message, variant: "destructive" });
                    }

                    forceReloadData();
                    toast({ title: "Sucesso!", description: `${allNewReceipts.length} recebimentos foram lançados.` });
                }
            } catch (error) {
                console.error('Erro crítico ao salvar recebimentos:', error);
                toast({ title: "Erro Crítico", description: "Ocorreu um erro inesperado ao salvar os recebimentos.", variant: "destructive" });
            } finally {
                // Notificar conclusão para fechar modais
                callbacks?.onSuccess?.(failedRows);
                callbacks?.onComplete?.(failedRows);
            }
        } else {
            callbacks?.onComplete?.(failedRows);
        }
    };

    const handleUpdateReceipt = async (receiptData, receiptId, callbacks) => {
        let hasError = false;
        try {
            const { data: userAliases } = await supabase.from('aliases').select('*').eq('user_id', activeAccountId);
            const preloadedAliases = userAliases || [];
            
            const processingContext = { ...dataRepos, user, activeAccountId, flags: dataRepos.flags, paymentMethods: dataRepos.paymentMethods, handleAddFlag, handleAddPaymentMethod, openAliasModal, preloadedAliases };

            if (receiptData.codigo_autorizacao) receiptData.codigo_autorizacao = removeLeadingZeros(receiptData.codigo_autorizacao);
            if (receiptData.codigopedido) receiptData.codigopedido = removeLeadingZeros(receiptData.codigopedido);

            const processedData = await processReceiptData(receiptData, processingContext, true);
            const { data, error } = await supabase.from('receipts').update(processedData).eq('id', receiptId).select('*, operators(name)').single();

            if (error) {
                hasError = true;
                throw error;
            }

            // Recalcular as vendas afetadas via RPC
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            forceReloadData();

            toast({ title: "Sucesso!", description: "Recebimento atualizado e vendas sincronizadas." });
        } catch (error) {
            hasError = true;
            toast({ title: "Erro ao atualizar recebimento", description: error.message, variant: "destructive" });
        } finally {
            if (!hasError && callbacks?.onSuccess) {
                callbacks.onSuccess();
            }
        }
    };

    const handleDeleteReceipt = async (receiptId) => {
        const { error } = await supabase.from('receipts').delete().eq('id', receiptId);
        if (error) {
            toast({ title: "Erro ao remover recebimento", description: error.message, variant: "destructive" });
        } else {
            // Recalcular as vendas afetadas via RPC
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            forceReloadData();
            toast({ title: "Sucesso!", description: "Recebimento removido e vendas atualizadas." });
        }
    };

    const handleDeleteReceiptsBatch = async (receiptIds) => {
        const { error } = await supabase.from('receipts').delete().in('id', receiptIds);
        if (error) {
            toast({ title: "Erro ao remover recebimentos", description: error.message, variant: "destructive" });
        } else {
            // Recalcular as vendas afetadas via RPC
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            forceReloadData();
            toast({ title: "Sucesso!", description: `${receiptIds.length} recebimentos removidos e vendas atualizadas.` });
        }
    };

    const handleAddOperator = async (newOperator) => {
        const { data, error } = await supabase.from('operators').insert({ name: newOperator.name, user_id: activeAccountId }).select().single();
        if (error) {
            toast({ title: "Erro ao adicionar operadora", description: error.message, variant: "destructive" });
        } else {
            setOperators(prev => [...prev, { ...data, flagIds: [] }]);
            toast({ title: "Sucesso!", description: "Operadora cadastrada." });
        }
    };

    const handleRemoveOperator = async (operatorId) => {
        const { error } = await supabase.from('operators').delete().eq('id', operatorId);
        if (error) {
            if (error.code === '23503') {
                toast({ title: "Ação Bloqueada", description: "A Operadora contém vínculos com recebimentos e não pode ser deletada.", variant: "destructive" });
            } else {
                toast({ title: "Erro ao remover operadora", description: error.message, variant: "destructive" });
            }
        } else {
            setOperators(prev => prev.filter(op => op.id !== operatorId));
            toast({ title: "Sucesso!", description: "Operadora removida." });
        }
    };

    const handleUpdateOperator = async (id, updatedData) => {
        const { error: deleteError } = await supabase.from('operator_flags').delete().eq('operator_id', id);
        if (deleteError) {
            toast({ title: "Erro ao atualizar vínculos", description: deleteError.message, variant: "destructive" });
            return;
        }
        if (updatedData.flagIds && updatedData.flagIds.length > 0) {
            const links = updatedData.flagIds.map(flag_id => ({ operator_id: id, flag_id, user_id: activeAccountId }));
            const { error: insertError } = await supabase.from('operator_flags').insert(links);
            if (insertError) {
                toast({ title: "Erro ao criar novos vínculos", description: insertError.message, variant: "destructive" });
                return;
            }
        }
        setOperators(prev => prev.map(op => op.id === id ? { ...op, flagIds: updatedData.flagIds } : op));
        toast({ title: "Sucesso!", description: "Operadora atualizada." });
    };

    const handleRemoveFlag = async (flagId) => {
        const { error } = await supabase.from('flags').delete().eq('id', flagId);
        if (error) {
            toast({ title: "Erro ao remover bandeira", description: error.message, variant: "destructive" });
        } else {
            setFlags(prev => prev.filter(f => f.id !== flagId));
            toast({ title: "Sucesso!", description: "Bandeira removida." });
        }
    };

    const handleRemovePaymentMethod = async (methodId) => {
        const { error } = await supabase.from('payment_methods').delete().eq('id', methodId);
        if (error) {
            toast({ title: "Erro ao remover forma de pagamento", description: error.message, variant: "destructive" });
        } else {
            setPaymentMethods(prev => prev.filter(m => m.id !== methodId));
            toast({ title: "Sucesso!", description: "Forma de pagamento removida." });
        }
    };

    const handleLinkOrphanReceipts = async (orphanReceipts, operatorsList, callbacks) => {
        if (!orphanReceipts || orphanReceipts.length === 0) {
            callbacks?.onSuccess?.(0);
            return;
        }

        try {
            // Aciona a reconciliação global via RPC
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            forceReloadData();
            
            toast({ title: "Processamento Concluído", description: "Tentativa de vinculação finalizada com sucesso." });
            callbacks?.onSuccess?.(orphanReceipts.length);

        } catch (error) {
            console.error('Erro ao verificar vínculo:', error);
            toast({ title: "Verificação concluída com erros", description: "Ocorreu um erro no processamento.", variant: "destructive" });
            callbacks?.onError?.(error);
        } finally {
            callbacks?.onComplete?.();
        }
    };

    const handleDeleteAllSales = async () => {
        const { error } = await supabase.from('sales').delete().eq('user_id', activeAccountId);
        if (error) {
            toast({ title: "Erro ao remover todas as vendas", description: error.message, variant: "destructive" });
        } else {
            forceReloadData();
            toast({ title: "Sucesso!", description: "Absolutamente todas as vendas foram removidas do sistema." });
        }
    };

    const handleDeleteAllReceipts = async () => {
        const { error } = await supabase.from('receipts').delete().eq('user_id', activeAccountId);
        if (error) {
            toast({ title: "Erro ao remover todos os recebimentos", description: error.message, variant: "destructive" });
        } else {
            // Aciona a reconciliação global via RPC para limpar os valores recebidos das vendas
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            forceReloadData();
            toast({ title: "Sucesso!", description: "Absolutamente todos os recebimentos foram removidos do sistema." });
        }
    };

    const handleSavePlatformAdjustments = async (adjustmentsData, operatorId) => {
        if (!adjustmentsData || adjustmentsData.length === 0) return;

        const records = adjustmentsData.map(adj => {
            let dataFormatada = adj.data_ocorrencia;
            // Converte DD/MM/YYYY para YYYY-MM-DD para o PostgreSQL aceitar
            if (dataFormatada && dataFormatada.includes('/')) {
                const parts = dataFormatada.split('/');
                if (parts.length === 3) {
                    dataFormatada = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
            }

            return {
                user_id: activeAccountId,
                operator_id: Number(operatorId),
                data_ocorrencia: dataFormatada,
                descricao: adj.descricao || 'Despesa iFood',
                valor_desconto: parseFloat(String(adj.valor_desconto || '0').replace(',', '.')),
                codigo_pedido: adj.codigo_pedido || null
            };
        });

        const { error } = await supabase.from('platform_adjustments').insert(records);

        if (error) {
            console.error('Erro ao salvar despesas de plataforma:', error);
            toast({ title: "Erro ao salvar despesas", description: error.message, variant: "destructive" });
        }
    };

    return {
        handleSaveSales,
        handleUpdateSale,
        handleDeleteSale,
        handleDeleteSalesBatch,
        handleDeleteAllSales,
        handleSaveReceipts,
        handleUpdateReceipt,
        handleDeleteReceipt,
        handleDeleteReceiptsBatch,
        handleDeleteAllReceipts,
        handleAddOperator,
        handleRemoveOperator,
        handleUpdateOperator,
        handleAddFlag,
        handleRemoveFlag,
        handleAddPaymentMethod,
        handleRemovePaymentMethod,
        handleLinkOrphanReceipts,
        handleSavePlatformAdjustments,
    };
};
